import { v4 as uuid } from 'uuid'
import api, { requestUrlPreviewGeneration, resolveUrl } from 'api/api'
import { getText, getImages } from '../../lib/value'
import { pick } from 'lodash'
import { getCachedQuest } from 'state/cache'
import questModel from 'components/quests/questModel'
import { extractKnovEmbedTarget, extractUrls, twitterRegexp } from 'components/shared/LinkPreview'
import {
    answerMatchesAnyKnovEmbedTarget,
    filterEmbedsForVisibleTargets,
    getKnovEmbedTargets,
    resolveKnovEmbedLinks,
} from 'components/answers/knovEmbedLinks'
import {
    addSuppressedPreviewUrl,
    areSamePreviewUrl,
    filterSuppressedPreviewUrls,
} from './payloadRemoval'
import { currentUserSnapshot } from 'lib/currentUserSnapshot'

// These are the properties which can be editied in the front end.
export const EDIT_WHITELIST = [
    'content',
    'delta_json',
    'action_requests',
    'position',
    'embeds',
    'files',
    'file_urls',
    'answer_image',
    'answer_image_url',
    'pending_images',
    'local_image_urls',
    'recording',
    'recording_url',
    'suppressed_preview_urls',
    'url',
    'url_id',
    'team_id',
    'team',
    'private',
    'public',
    'dice',
    'message_type',
    'poll_options',
    'poll_duration_minutes',
]

// These are the answer properties that can be inherited from published answer.
// We do this to limit the ways in which a draftAnswer can interact with the app.
export const DRAFT_WHITELIST = [
    ...EDIT_WHITELIST,
    'space_id',
    'mp4_recording',
    'mp4_recording_url',
    'space_id',
    'user_id',
    'user',
    'created_at',
    'updated_at',
]

/** @typedef {import('../../types/answers').Answer} Answer */
/** @typedef {import('../../types/answers').DraftAnswer} DraftAnswer */

const model = {
    /** @param {Partial<Answer>} answer */
    getDraft: answer => model.newAnswer(pick(answer, DRAFT_WHITELIST)),

    getDeltaText: deltaJson => getText(deltaJson),

    getDeltaJson: editor => editor?.getContents() || {},

    getText: answer => model.getDeltaText(answer?.delta_json),

    empty: answer => model.getDeltaText(answer?.delta_json)?.trim() == '',

    hasText: answer => answer.content?.length || getText(answer.deltaJson),
    hasEmbeds: answer => answer.embeds?.length,
    hasFiles: answer => answer.file_urls?.length,
    hasVideo: answer => answer.recording_url,
    hasImage: answer =>
        answer.answer_image_url ||
        answer.local_image_urls?.length ||
        answer.pending_images?.length ||
        getImages(answer.deltaJson),
    hasUrl: answer => answer.url,
    hasDice: answer => answer.dice && answer.dice.type === 'title_upvalue_raffle',
    hasPoll: answer =>
        answer?.message_type === 'poll' &&
        Array.isArray(answer?.poll_options) &&
        answer.poll_options.some(option => option?.trim?.().length > 0),
    hasAiActions: answer => {
        const actionRequests = answer?.action_requests
        return Array.isArray(actionRequests) && actionRequests.some(action => action?.tag)
    },

    hasContent: answer => {
        let hasContent = null

        if (answer) {
            hasContent =
                model.hasText(answer) ||
                model.hasEmbeds(answer) ||
                model.hasFiles(answer) ||
                model.hasVideo(answer) ||
                model.hasImage(answer) ||
                model.hasUrl(answer) ||
                model.hasDice(answer) ||
                model.hasPoll(answer) ||
                model.hasAiActions(answer)
        }
        return hasContent
    },

    hasAnnotation: (answer, editor = null) => {
        let deltaJson
        if (editor) {
            deltaJson = model.getDeltaJson(editor)
        } else {
            deltaJson = answer?.delta_json
        }
        const deltaText = model.getDeltaText(deltaJson)
        return answer?.url && !!deltaText?.trim()
    },

    getPreviewUrls: deltaJson => {
        const extracted = extractUrls(deltaJson || {})
        return [
            ...(extracted.nonKnovLinks || []),
            ...(extracted.knovEmbedLinks || []),
            ...(extracted.streamEmbedLinks || []),
            ...(extracted.leaderboardEmbedLinks || []),
            ...(extracted.tdpEmbedLinks || []),
        ].filter(Boolean)
    },

    answerContainsUrlText: (deltaJson, targetUrl) => {
        if (!targetUrl) return false
        return model.getPreviewUrls(deltaJson).some(url => areSamePreviewUrl(url, targetUrl))
    },

    appendUrlToEditorText: (editor, targetUrl) => {
        if (!editor || !targetUrl) return false

        const currentDelta = editor.getContents ? editor.getContents() : {}
        if (model.answerContainsUrlText(currentDelta, targetUrl)) {
            return false
        }

        const currentText = model.getDeltaText(currentDelta || {}) || ''
        const separator = currentText && !/\s$/.test(currentText) ? '\n' : ''
        const linkContent = {
            type: 'text',
            text: targetUrl,
            marks: [
                {
                    type: 'link',
                    attrs: {
                        href: targetUrl,
                    },
                },
            ],
        }
        const rawEditor = editor.getEditor ? editor.getEditor() : editor

        if (rawEditor?.chain) {
            const contentToInsert = separator
                ? [{ type: 'text', text: separator }, linkContent]
                : [linkContent]
            return rawEditor.chain().focus('end').insertContent(contentToInsert).run()
        }

        if (editor.commands?.insertContent) {
            const contentToInsert = separator
                ? [{ type: 'text', text: separator }, linkContent]
                : [linkContent]
            editor.commands.focus?.('end')
            editor.commands.insertContent(contentToInsert)
            return true
        }

        if (editor.insertText && editor.getLength && editor.setSelection) {
            const insertAt = Math.max((editor.getLength() || 1) - 1, 0)
            if (separator) {
                editor.insertText(insertAt, separator)
            }
            const linkInsertAt = separator ? insertAt + separator.length : insertAt
            editor.insertText(linkInsertAt, targetUrl, 'link', targetUrl)
            editor.setSelection(linkInsertAt + targetUrl.length, 0)
            return true
        }

        return false
    },

    getVisibleKnovEmbedLinks: (deltaJson, hiddenPreviewUrls = []) => {
        const extracted = extractUrls(deltaJson || {})
        return filterSuppressedPreviewUrls(extracted.knovEmbedLinks || [], hiddenPreviewUrls || [])
    },

    waitForVisibleEmbeds: async (options = {}) => {
        const {
            deltaJson,
            hiddenPreviewUrls = [],
            isEmbedsLoading,
            getEmbedLoadingPromise,
            setWaitingForEmbeds,
        } = options
        const isEmbedsLoadingFn =
            typeof isEmbedsLoading === 'function' ? isEmbedsLoading : () => false
        const getEmbedLoadingPromiseFn =
            typeof getEmbedLoadingPromise === 'function' ? getEmbedLoadingPromise : () => null
        const setWaitingForEmbedsFn =
            typeof setWaitingForEmbeds === 'function' ? setWaitingForEmbeds : _waiting => undefined
        const visibleKnovEmbedLinks = model.getVisibleKnovEmbedLinks(deltaJson, hiddenPreviewUrls)

        if (visibleKnovEmbedLinks.length > 0 && !isEmbedsLoadingFn()) {
            setWaitingForEmbedsFn(true)
            await new Promise(resolve => {
                const checkInterval = setInterval(() => {
                    if (isEmbedsLoadingFn() && getEmbedLoadingPromiseFn()) {
                        clearInterval(checkInterval)
                        resolve()
                    }
                }, 50)

                setTimeout(() => {
                    clearInterval(checkInterval)
                    resolve()
                }, 1000)
            })
        }

        const embedLoadingPromise = getEmbedLoadingPromiseFn()
        if (isEmbedsLoadingFn() && embedLoadingPromise) {
            setWaitingForEmbedsFn(true)
            try {
                await embedLoadingPromise
            } catch (e) {}
        }

        return visibleKnovEmbedLinks
    },

    ensureEmbedsForLinks: async (options = {}) => {
        const {
            knovEmbedLinks,
            filterEmbeds,
            onEmbedsResolved,
            onError,
            setWaitingForEmbeds,
            fetchQuest,
            fetchAnswer,
            fetchAnonymousShareAnswer,
        } = options
        const filterEmbedsFn = typeof filterEmbeds === 'function' ? filterEmbeds : embeds => embeds
        const onEmbedsResolvedFn =
            typeof onEmbedsResolved === 'function' ? onEmbedsResolved : _embeds => undefined
        const onErrorFn = typeof onError === 'function' ? onError : _error => undefined
        const setWaitingForEmbedsFn =
            typeof setWaitingForEmbeds === 'function' ? setWaitingForEmbeds : _waiting => undefined
        const fetchQuestFn = typeof fetchQuest === 'function' ? fetchQuest : api.getQuest
        const fetchAnswerFn = typeof fetchAnswer === 'function' ? fetchAnswer : api.getAnswer
        const fetchAnonymousShareAnswerFn =
            typeof fetchAnonymousShareAnswer === 'function'
                ? fetchAnonymousShareAnswer
                : api.getAnswerByAnonymousShareToken

        if (!knovEmbedLinks || knovEmbedLinks.length === 0) {
            return []
        }

        setWaitingForEmbedsFn(true)
        try {
            const embeds = (
                await resolveKnovEmbedLinks(knovEmbedLinks, {
                    fetchQuest: fetchQuestFn,
                    fetchAnswer: fetchAnswerFn,
                    fetchAnonymousShareAnswer: fetchAnonymousShareAnswerFn,
                })
            ).filter(Boolean)
            if (embeds.length !== knovEmbedLinks.length) {
                throw new Error('Missing embed data for one or more links.')
            }

            const nextEmbeds = filterEmbedsFn(embeds)
            onEmbedsResolvedFn(nextEmbeds)
            return nextEmbeds
        } catch (e) {
            onErrorFn(e)
            return null
        } finally {
            setWaitingForEmbedsFn(false)
        }
    },

    getComposeAttachmentState: ({
        answer,
        draftAnswer = null,
        isEditing = false,
        extractedUrls = { nonKnovLinks: [], knovEmbedLinks: [] },
        hiddenPreviewUrls = [],
        resolveEmbedAnswer = embed => embed,
    }) => {
        const attachmentAnswer = (isEditing ? draftAnswer || answer : answer) || null
        const hiddenUrls = hiddenPreviewUrls || []
        const linkPreviews = filterSuppressedPreviewUrls(
            extractedUrls?.nonKnovLinks || [],
            hiddenUrls,
        )
        const visibleEmbedTargets = getKnovEmbedTargets(
            filterSuppressedPreviewUrls(extractedUrls?.knovEmbedLinks || [], hiddenUrls),
        )
        const hiddenEmbedTargets = (extractedUrls?.knovEmbedLinks || [])
            .filter(url => hiddenUrls.includes(url))
            .map(extractKnovEmbedTarget)
            .filter(Boolean)
        const embedMatchesTargets = (embed, targets) =>
            answerMatchesAnyKnovEmbedTarget(resolveEmbedAnswer(embed), targets)
        const sourceEmbeds = attachmentAnswer?.embeds || []
        const embeds =
            visibleEmbedTargets.length > 0
                ? filterEmbedsForVisibleTargets(
                      sourceEmbeds,
                      visibleEmbedTargets,
                      resolveEmbedAnswer,
                  )
                : sourceEmbeds.filter(embed => !embedMatchesTargets(embed, hiddenEmbedTargets))
        const attachedUrl =
            isEditing && draftAnswer
                ? attachmentAnswer?.url || null
                : attachmentAnswer?.url || answer?.url || null

        return {
            attachmentAnswer,
            imageUrl: attachmentAnswer?.answer_image_url || null,
            videoUrl:
                attachmentAnswer?.mp4_recording_url || attachmentAnswer?.recording_url || null,
            fileUrl: attachmentAnswer?.file_urls?.[0] || null,
            linkPreviews,
            embeds,
            attachedUrl,
            attachedUrlAddress: attachedUrl?.address || null,
        }
    },

    getPrimaryUrlAttachmentIntent: attachmentState => {
        const attachedUrl = attachmentState?.attachedUrl || null
        const attachedUrlAddress =
            attachedUrl?.address || attachmentState?.attachedUrlAddress || null
        if (attachedUrlAddress) {
            return { url: attachedUrl, address: attachedUrlAddress }
        }

        const previewUrl = attachmentState?.linkPreviews?.[0] || null
        if (!previewUrl) {
            return { url: null, address: null }
        }

        return {
            url: null,
            address: previewUrl,
        }
    },

    ensureUrlAttachmentForSubmit: async attachmentState => {
        const attachmentIntent = model.getPrimaryUrlAttachmentIntent(attachmentState)
        if (!attachmentIntent.address) {
            return { url: null, url_id: null }
        }

        const existingUrlId = attachmentIntent.url?.id || attachmentIntent.url?.url_id || null
        if (existingUrlId) {
            return {
                url: attachmentIntent.url,
                url_id: existingUrlId,
            }
        }

        const resolver =
            typeof attachmentIntent.address === 'string' &&
            twitterRegexp.test(attachmentIntent.address)
                ? resolveUrl
                : requestUrlPreviewGeneration
        const generatedPreview = await resolver(attachmentIntent.address)
        const generatedUrlId = generatedPreview?.url_id || null
        if (!generatedUrlId) {
            return null
        }
        const generatedUrl = generatedPreview?.['url'] || null

        return {
            url: {
                ...(attachmentIntent.url || {}),
                ...(generatedUrl || {}),
                id: generatedUrlId,
                address: generatedUrl?.address || attachmentIntent.address,
            },
            url_id: generatedUrlId,
        }
    },

    mergeHiddenPreviewUrls: (...urlCollections) => {
        return urlCollections
            .flat()
            .filter(Boolean)
            .reduce((mergedUrls, url) => {
                return addSuppressedPreviewUrl(mergedUrls, url)
            }, [])
    },

    /**
     * @param {Partial<Answer>} [answer]
     * @returns {DraftAnswer}
     */
    newAnswer: (answer = {}) => {
        const answerId = uuid()
        /** @type {DraftAnswer} */
        let localAnswer = {
            id: answerId,
            ...answer,
            is_draft: true,
        }

        const now = new Date()
        if (!localAnswer.created_at) localAnswer.created_at = now
        if (!localAnswer.updated_at) localAnswer.updated_at = now
        if (!localAnswer.position) localAnswer.position = now.getTime()

        if (!localAnswer.user_id) localAnswer.user_id = gon.currentUser?.id
        if (!localAnswer.user) localAnswer.user = currentUserSnapshot()

        if (!localAnswer.path) localAnswer.path = `/answer/${answerId}`
        if (!localAnswer.archive) localAnswer.archive = false

        return localAnswer
    },

    hasLLMRequest: answer => {
        return answer.message_type !== 'agent' && answer.agent_model
    },

    hasLLMReply: answer => {
        return answer.message_type === 'agent' && answer.agent_model
    },

    // Returns number of replies for the child quest of this answer
    childCount: answer => {
        try {
            const childQuestId = answer?.child_quests?.[0]?.id
            if (!childQuestId) return 0
            const childQuest = getCachedQuest(childQuestId)
            if (!childQuest) return 0
            const replies = questModel.showUnarchivedAnswers(childQuest)
            return replies?.length || 0
        } catch (e) {
            return 0
        }
    },
}

export default model
